import { el } from "./dom";

export interface PasteButtonOptions {
  readonly label: string;
  /** Called with the sanitized clipboard contents after a successful read. */
  readonly onPaste: (text: string) => void;
  /**
   * Optional sanitizer — defaults to trim + strip whitespace. Called before
   * `onPaste`; the result is what gets handed back.
   */
  readonly sanitize?: (raw: string) => string;
  readonly className?: string;
}

const defaultSanitize = (raw: string): string => raw.replace(/\s+/g, "").trim();

async function readClipboard(): Promise<string | null> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.readText) {
    try {
      return await navigator.clipboard.readText();
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Paste-from-clipboard button — reads via the Clipboard API and hands the
 * sanitized text to `onPaste`. The browser may prompt the user the first time
 * the page tries to read clipboard contents; permission errors silently no-op.
 */
export function createPasteButton(opts: PasteButtonOptions): HTMLButtonElement {
  const classes = ["crest-btn", "crest-btn--small"];
  if (opts.className) classes.push(opts.className);

  const btn = el("button", {
    class: classes.join(" "),
    attrs: { type: "button" },
  }, [opts.label]) as HTMLButtonElement;

  btn.addEventListener("click", () => {
    void readClipboard().then((text) => {
      if (text === null) return;
      const sanitized = (opts.sanitize ?? defaultSanitize)(text);
      if (sanitized.length === 0) return;
      opts.onPaste(sanitized);
    });
  });

  return btn;
}
